import type {
  AuthGateway,
  AuthenticatedSession,
  SystemAnnouncement,
  TenantSummary,
  UserSummary,
} from '@forq/contracts';
import type { PlatformDataGateway } from '@forq/platform-data';

export interface ApplicationBootstrapDependencies {
  readonly auth: AuthGateway;
  readonly platformData: PlatformDataGateway;
}

export interface ReadyApplicationState {
  readonly kind: 'ready';
  readonly session: AuthenticatedSession;
  readonly user: UserSummary;
  readonly tenants: readonly TenantSummary[];
  readonly activeTenantId: string;
  readonly permissions: ReadonlySet<string>;
  /** Die Systemmeldung des aktiven Mandanten, oder null. Wird nach dem Start angezeigt. */
  readonly announcement: SystemAnnouncement | null;
}

export type ApplicationBootstrapResult =
  | { readonly kind: 'anonymous' }
  | {
      readonly kind: 'no-tenant';
      readonly session: AuthenticatedSession;
      readonly user: UserSummary;
    }
  | ReadyApplicationState;

/**
 * Die Schritte, die beim Start WIRKLICH passieren — in dieser Reihenfolge. Der Startbildschirm
 * zeigt sie mit den echten Ergebnissen an (Name des Mandanten, Anzahl der Rechte), statt einen
 * Spinner zu drehen: das ist ehrlicher und wirkt kompetenter als ein Balken, der nichts weiss.
 */
export type BootStageId = 'session' | 'tenants' | 'permissions';

export interface BootStageEvent {
  readonly stage: BootStageId;
  /** Was dabei herauskam — echte Werte, keine Platzhalter. */
  readonly detail: string;
}

export type BootProgress = (event: BootStageEvent) => void;

function displayNameFor(session: AuthenticatedSession): string {
  const explicitName = session.displayName?.trim();
  if (explicitName) return explicitName;

  const emailName = session.email?.split('@')[0]?.trim();
  return emailName || 'FORQ Benutzer';
}

function toUserSummary(session: AuthenticatedSession): UserSummary {
  return {
    id: session.userId,
    displayName: displayNameFor(session),
    role: 'FORQ Benutzer',
  };
}

function selectActiveTenant(
  tenants: readonly TenantSummary[],
  preferredTenantId?: string | null,
): TenantSummary {
  const selected = tenants.find((tenant) => tenant.id === preferredTenantId) ?? tenants[0];
  if (!selected) throw new Error('No tenant is available.');
  return selected;
}

export async function loadSessionApplication(
  session: AuthenticatedSession,
  platformData: PlatformDataGateway,
  preferredTenantId?: string | null,
  onProgress?: BootProgress,
): Promise<ApplicationBootstrapResult> {
  const user = toUserSummary(session);
  onProgress?.({ stage: 'session', detail: user.displayName });

  const tenants = await platformData.listTenantsForUser(session.userId);
  if (tenants.length === 0) {
    return { kind: 'no-tenant', session, user };
  }

  const activeTenant = selectActiveTenant(tenants, preferredTenantId);
  onProgress?.({
    stage: 'tenants',
    detail: tenants.length === 1
      ? activeTenant.name
      : `${activeTenant.name} · ${tenants.length} Firmen verfügbar`,
  });

  const permissions = await platformData.resolvePermissions(session.userId, activeTenant.id);
  onProgress?.({
    stage: 'permissions',
    detail: `${permissions.size} ${permissions.size === 1 ? 'Recht' : 'Rechte'}`,
  });

  return {
    kind: 'ready',
    session,
    user,
    tenants,
    activeTenantId: activeTenant.id,
    permissions,
    announcement: await loadAnnouncementSafely(platformData, activeTenant.id),
  };
}

/**
 * Die Systemmeldung ist Beiwerk -- die Arbeit ist das ERP. Faellt sie aus (RPC fehlt nach einem
 * halben Deploy, Netz weg, Datenmuell), wird der Start deshalb NICHT abgebrochen: sonst sperrt eine
 * kaputte Beta-Warnung die ganze Firma aus. Fehler landen in der Konsole, nicht im Weg des Nutzers.
 */
async function loadAnnouncementSafely(
  platformData: PlatformDataGateway,
  tenantId: string,
): Promise<SystemAnnouncement | null> {
  try {
    return await platformData.loadActiveAnnouncement(tenantId);
  } catch (error) {
    console.warn('Die Systemmeldung konnte nicht geladen werden. Der Start läuft weiter.', error);
    return null;
  }
}

export async function bootstrapApplication(
  dependencies: ApplicationBootstrapDependencies,
  preferredTenantId?: string | null,
  onProgress?: BootProgress,
): Promise<ApplicationBootstrapResult> {
  const session = await dependencies.auth.getSession();
  if (!session) return { kind: 'anonymous' };
  return loadSessionApplication(session, dependencies.platformData, preferredTenantId, onProgress);
}
